import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronRight, ChevronDown, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import StatCard from './StatCard';
import DateRangeFilter from './DateRangeFilter';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';
import { useAuth, userIsAdmin, userIsEngineer, userIsReviewer, userIsSupervisor, userIsManager } from '../../context/AuthContext';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';

function fmt(amount) {
  if (amount == null) return '$0.00';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: '600', whiteSpace: 'nowrap' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

const STATUS_COLOR_MAP = {
  pending: '#eab308',           // yellow
  approved: '#22c55e',          // green
  paid: '#0d9488',              // teal
  rejected: '#dc2626',          // red
  pending_review: '#7c3aed',    // purple
  partially_approved: '#2563eb', // blue
  sent_back: '#ea580c',         // orange
  partially_returned: '#d97706', // amber
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 600, color: NAVY, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#475569' }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
}

// Compact multi-select dropdown for picking employees in the Nómina export filter.
function EmployeeMultiSelect({ employees, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedSet = new Set(selected);
  const filtered = employees.filter(e => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) || (e.external_id || '').toLowerCase().includes(q);
  });
  const label = disabled
    ? 'Empleados (Solo WIOA)'
    : selected.length === 0 ? 'Todos los empleados' : `${selected.length} empleado${selected.length !== 1 ? 's' : ''}`;

  function toggle(id) {
    if (selectedSet.has(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${BORDER_COLOR}`,
          color: disabled ? '#94a3b8' : NAVY, background: disabled ? '#f1f5f9' : '#f8fafc',
          cursor: disabled ? 'not-allowed' : 'pointer', minWidth: 150, textAlign: 'left',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {label}
      </button>
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 50,
          background: '#fff', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 260, maxHeight: 300, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${BORDER_COLOR}` }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar..."
              autoFocus
              style={{ width: '100%', fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${BORDER_COLOR}`, boxSizing: 'border-box' }}
            />
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange([])} style={{
                marginTop: 6, background: 'none', border: 'none', color: '#64748b',
                fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'Inter, sans-serif',
              }}>
                Limpiar selección ({selected.length})
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>Sin resultados</div>
            )}
            {filtered.map(e => (
              <label key={e.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                fontSize: 12, color: NAVY, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={selectedSet.has(e.id)} onChange={() => toggle(e.id)} />
                <span>{e.name}{e.external_id ? <span style={{ color: '#94a3b8' }}> · {e.external_id}</span> : null}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin red-flag widget: one-week lookback of projects with missing/draft daily sheets,
// grouped by project (worst-first), one chip per overdue working day. Live current-state —
// chips clear on submit/refresh; a day only appears once past its 10:00 AM AST deadline.
function LateNominaFlags({ data }) {
  if (!data) return null;

  // Empty / all-clear.
  if (!data.flags || data.flags.length === 0) {
    return (
      <div style={{ ...bubbleStyle, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 14, fontWeight: 600 }}>
        <CheckCircle2 size={17} /> Todas las tarjetas al día.
      </div>
    );
  }

  // "Lun 2" — abbreviated Spanish weekday + day number, parsed at noon UTC (tz-safe).
  const chipLabel = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00Z');
    const wd = d.toLocaleDateString('es', { timeZone: 'UTC', weekday: 'short' }).replace('.', '');
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getUTCDate()}`;
  };
  const chipStyle = (reason) => ({
    borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    ...(reason === 'draft'
      ? { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }
      : { background: '#dc2626', color: '#fff', border: '1px solid #dc2626' }),
  });

  return (
    <div style={{ ...bubbleStyle, padding: 0, marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #fecaca' }}>
        <AlertTriangle size={17} style={{ color: '#dc2626' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>Nómina atrasada</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#991b1b' }}>
          {data.total_projects} {data.total_projects === 1 ? 'proyecto' : 'proyectos'} · {data.total_cards} {data.total_cards === 1 ? 'tarjeta' : 'tarjetas'}
        </span>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {data.flags.map(f => (
          <div key={f.project_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px', borderTop: '1px solid #fee2e2', fontSize: 13 }}>
            <div style={{ width: 150, flexShrink: 0 }}>
              <div style={{ fontWeight: 600, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.project_name}</div>
              <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.responsible_name}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1 }}>
              {f.overdue_days.map(od => (
                <span key={od.date} title={od.reason === 'draft' ? 'En borrador' : 'Sin tarjeta'} style={chipStyle(od.reason)}>
                  {chipLabel(od.date)}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, padding: '8px 16px', borderTop: '1px solid #fecaca', fontSize: 11, color: '#64748b' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: '#dc2626', display: 'inline-block' }} /> rojo = Sin tarjeta
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: '#fffbeb', border: '1px solid #fde68a', display: 'inline-block' }} /> ámbar = En borrador
        </span>
      </div>
    </div>
  );
}

export default function RoleSummaryTab({ initialTab = 'gastos', hideTabs = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEng = userIsEngineer(user);
  const isRev = userIsReviewer(user);
  const isAdm = userIsAdmin(user);
  const isSup = userIsSupervisor(user);
  const isMgr = userIsManager(user);
  const hasNomina = isSup || isEng || isMgr || isRev || isAdm;

  const [tab, setTab] = useState(initialTab);

  // Gastos projects (for filter)
  const [gastosProjects, setGastosProjects] = useState([]);
  const [gastosProjectFilter, setGastosProjectFilter] = useState('');
  useEffect(() => {
    if (tab !== 'gastos' || gastosProjects.length > 0) return;
    client.get('/api/projects')
      .then(r => setGastosProjects(r.data.filter(p => p.active !== false)))
      .catch(() => {});
  }, [tab]);

  // Gastos stats
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  useEffect(() => {
    if (tab !== 'gastos') return;
    setLoading(true);
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (gastosProjectFilter) params.projectId = gastosProjectFilter;
    client.get('/api/receipts/dashboard-stats', { params })
      .then(r => setStats(r.data))
      .catch(err => {
        console.error('Dashboard stats error:', err);
        appInsights?.trackException({
          exception: err,
          properties: {
            context: 'dashboard_stats_fetch',
            module: 'platform_dashboard_gastos',
            user_role: user?.roles?.join(','),
            project_filter: gastosProjectFilter || null,
          },
        });
      })
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, gastosProjectFilter, tab]);

  // Nómina projects list (for filter)
  const [nominaProjects, setNominaProjects] = useState([]);
  const [nominaProjectFilter, setNominaProjectFilter] = useState('');
  useEffect(() => {
    if (tab !== 'nomina' || nominaProjects.length > 0) return;
    client.get('/api/nomina/my-projects')
      .then(r => setNominaProjects(r.data))
      .catch(() => {});
  }, [tab]);

  // Nómina employees list + export filters (employee selection / Solo WIOA)
  const [nominaEmployees, setNominaEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [wioaOnly, setWioaOnly] = useState(false);
  useEffect(() => {
    if (tab !== 'nomina' || nominaEmployees.length > 0) return;
    client.get('/api/nomina/employees')
      .then(r => setNominaEmployees(r.data))
      .catch(() => {});
  }, [tab]);

  // Nómina stats
  const [nominaStats, setNominaStats] = useState(null);
  const [nominaLoading, setNominaLoading] = useState(false);
  const [nominaDateFrom, setNominaDateFrom] = useState(null);
  const [nominaDateTo, setNominaDateTo] = useState(null);
  useEffect(() => {
    if (tab !== 'nomina') return;
    setNominaLoading(true);
    const params = {};
    if (nominaDateFrom) params.dateFrom = nominaDateFrom;
    if (nominaDateTo) params.dateTo = nominaDateTo;
    if (nominaProjectFilter) params.project_id = nominaProjectFilter;
    // Solo WIOA overrides the explicit employee selection (same precedence as the export).
    if (wioaOnly) params.wioa_only = 'true';
    else if (selectedEmployeeIds.length > 0) params.employee_ids = selectedEmployeeIds.join(',');
    client.get('/api/nomina/dashboard-stats', { params })
      .then(r => setNominaStats(r.data))
      .catch(err => {
        console.error('Nomina dashboard error:', err);
        appInsights?.trackException({
          exception: err,
          properties: {
            context: 'dashboard_stats_fetch',
            module: 'platform_dashboard_nomina',
            user_role: user?.roles?.join(','),
            project_filter: nominaProjectFilter || null,
          },
        });
      })
      .finally(() => setNominaLoading(false));
  }, [nominaDateFrom, nominaDateTo, nominaProjectFilter, wioaOnly, selectedEmployeeIds.join(','), tab]);

  // Late-submitter red flags (admin only) — live current-state check, refreshes with the tab.
  const [lateFlags, setLateFlags] = useState(null);
  useEffect(() => {
    if (tab !== 'nomina' || !isAdm) return;
    client.get('/api/nomina/late-flags')
      .then(r => setLateFlags(r.data))
      .catch(err => {
        console.error('Late flags error:', err);
        appInsights?.trackException({ exception: err, properties: { context: 'late_flags_fetch', module: 'platform_dashboard_nomina' } });
      });
  }, [tab, isAdm]);

  // Calendar — week view
  const [calWeekStart, setCalWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const diff = (day + 6) % 7; // days back to Monday
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
  });
  const [calData, setCalData] = useState(null);
  useEffect(() => {
    if (tab !== 'nomina') return;
    // The week may span two months — fetch both if needed
    const startMonth = calWeekStart.slice(0, 7);
    const weekEnd = new Date(calWeekStart + 'T12:00:00Z');
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
    const endMonth = weekEnd.toISOString().slice(0, 7);

    const fetches = [client.get('/api/nomina/calendar-summary', { params: { month: startMonth } })];
    if (endMonth !== startMonth) {
      fetches.push(client.get('/api/nomina/calendar-summary', { params: { month: endMonth } }));
    }
    Promise.all(fetches).then(results => {
      const allDays = [];
      const seenDates = new Set();
      for (const r of results) {
        for (const day of (r.data.days || [])) {
          if (!seenDates.has(day.date)) {
            seenDates.add(day.date);
            allDays.push(day);
          }
        }
      }
      setCalData({ days: allDays, projects: results[0].data.projects });
    }).catch(err => console.error('Calendar error:', err));
  }, [calWeekStart, tab]);

  // Tab switcher style
  const tabBtn = (key, label) => ({
    background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
    fontSize: 14, fontWeight: tab === key ? 700 : 500, color: tab === key ? NAVY : '#94a3b8',
    padding: '6px 16px', borderBottom: tab === key ? `2px solid ${AMBER}` : '2px solid transparent',
  });

  // ── Export handler ──
  const [exporting, setExporting] = useState(false);
  async function handleExport() {
    setExporting(true);
    try {
      const isNomina = tab === 'nomina';
      // Use simple dashboard export (no side effects — does NOT mark items as paid/exported)
      const endpoint = isNomina ? '/api/nomina/export-csv' : '/api/receipts/export-csv';
      const params = {};
      if (isNomina) {
        if (nominaDateFrom) params.dateFrom = nominaDateFrom;
        if (nominaDateTo) params.dateTo = nominaDateTo;
        if (nominaProjectFilter) params.project_id = nominaProjectFilter;
        // Solo WIOA overrides the explicit employee selection.
        if (wioaOnly) params.wioa_only = 'true';
        else if (selectedEmployeeIds.length > 0) params.employee_ids = selectedEmployeeIds.join(',');
      } else {
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (gastosProjectFilter) params.project_id = gastosProjectFilter;
      }
      const res = await client.get(endpoint, { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = isNomina ? 'nomina_dashboard.csv' : 'gastos_dashboard.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert(err.response?.status === 403 ? 'No tienes permisos para exportar.' : 'Error al exportar.');
    } finally {
      setExporting(false);
    }
  }

  // ── Nómina Tab Render ──
  if (tab === 'nomina') {
    const NOMINA_STATUS_LABELS = {
      draft: 'Borrador', submitted: 'Pend. Ingeniero', pending_engineer: 'Pend. Ingeniero',
      pending_manager: 'Pend. Gerente', pending_clerk: 'Rev. Final', approved: 'Aprobado', returned: 'Devuelto',
    };
    const NOMINA_STATUS_COLORS = {
      draft: '#7c3aed', submitted: '#ca8a04', pending_engineer: '#2563eb',
      pending_manager: '#7c3aed', pending_clerk: '#0f766e', approved: '#16a34a', returned: '#ea580c',
    };

    return (
      <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {!hideTabs && <h2 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0, marginRight: 16 }}>Dashboard</h2>}
            {!hideTabs && hasNomina && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                <button onClick={() => setTab('gastos')} style={tabBtn('gastos', 'Gastos')}>Gastos</button>
                <button onClick={() => setTab('nomina')} style={tabBtn('nomina', 'Nómina')}>Nómina</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={nominaProjectFilter}
              onChange={e => setNominaProjectFilter(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${BORDER_COLOR}`, color: NAVY, background: '#f8fafc' }}
            >
              <option value="">Todos los proyectos</option>
              {nominaProjects.map(p => (
                <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ''}{p.name}</option>
              ))}
            </select>
            <DateRangeFilter
              dateFrom={nominaDateFrom} dateTo={nominaDateTo}
              onChange={({ dateFrom: df, dateTo: dt }) => { setNominaDateFrom(df); setNominaDateTo(dt); }}
            />
            {(isAdm || isRev || isEng) && (
              <EmployeeMultiSelect
                employees={nominaEmployees}
                selected={selectedEmployeeIds}
                onChange={setSelectedEmployeeIds}
                disabled={wioaOnly}
              />
            )}
            {(isAdm || isRev || isEng) && (
              <label style={{ fontSize: 12, color: NAVY, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={wioaOnly} onChange={e => setWioaOnly(e.target.checked)} />
                Solo WIOA
              </label>
            )}
            {(isAdm || isRev || isEng) && (
              <button onClick={handleExport} disabled={exporting} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: exporting ? '#94a3b8' : NAVY, color: '#fff',
                border: 'none', cursor: exporting ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}>
                <Download size={13} /> {exporting ? 'Exportando…' : 'Exportar CSV'}
              </button>
            )}
          </div>
        </div>

        {isAdm && <LateNominaFlags data={lateFlags} />}

        {nominaLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: AMBER }} />
          </div>
        ) : !nominaStats ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Error al cargar estadísticas de nómina</div>
        ) : (
          <>
            {/* Stat Cards — status-focused */}
            {(() => {
              const byStatus = {};
              for (const s of (nominaStats.byStatus || [])) byStatus[s.status] = s.count;
              const pending = (byStatus.submitted || 0) + (byStatus.pending_engineer || 0) + (byStatus.pending_manager || 0) + (byStatus.pending_clerk || 0);
              return (
                <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                  <StatCard label="Total Tarjetas" value={nominaStats.totals.sheetCount} />
                  <StatCard label="Pendientes" value={pending} highlight={pending > 0} />
                  <StatCard label="Aprobadas" value={byStatus.approved || 0} subtitle={`${nominaStats.totals.totalHours.toFixed(0)}h total`} subtitleLarge />
                  <StatCard label="Borrador" value={byStatus.draft || 0} />
                  <StatCard label="Devueltas" value={byStatus.returned || 0} highlight={byStatus.returned > 0} />
                  {nominaStats.pendingApprovals > 0 && (
                    <StatCard label="Mi Aprobación" value={nominaStats.pendingApprovals} highlight />
                  )}
                </div>
              );
            })()}

            {/* Calendar Summary */}
            {calData && (() => {
              const CAL_STATUS_COLORS = {
                approved: '#16a34a', submitted: '#ca8a04', pending_engineer: '#2563eb',
                pending_manager: '#7c3aed', pending_clerk: '#0f766e', draft: '#a78bfa', returned: '#ea580c',
              };
              const CAL_STATUS_LABELS = {
                approved: 'Aprobado', submitted: 'Sometido', pending_engineer: 'Pend. Ing.',
                pending_manager: 'Pend. Ger.', pending_clerk: 'Rev. Final', draft: 'Borrador', returned: 'Devuelto',
              };
              const rawDays = calData.days || [];
              // Filter by project if selected
              const allDays = nominaProjectFilter
                ? rawDays.map(d => ({ ...d, sheets: d.sheets.filter(s => String(s.project_id) === nominaProjectFilter) }))
                : rawDays;

              // Get the 7 days of the current week (Mon-Sun)
              const weekDays = [];
              for (let i = 0; i < 7; i++) {
                const d = new Date(calWeekStart + 'T12:00:00Z');
                d.setUTCDate(d.getUTCDate() + i);
                const dateStr = d.toISOString().slice(0, 10);
                const found = allDays.find(day => day.date === dateStr);
                weekDays.push(found || { date: dateStr, dayOfWeek: d.getUTCDay(), isWeekend: d.getUTCDay() === 0 || d.getUTCDay() === 6, holiday: null, sheets: [] });
              }

              const weekEndDate = weekDays[6]?.date;
              const fmtShort = (d) => new Date(d + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
              const weekLabel = `${fmtShort(calWeekStart)} — ${fmtShort(weekEndDate)}`;

              const prevWeek = () => {
                const d = new Date(calWeekStart + 'T12:00:00Z');
                d.setUTCDate(d.getUTCDate() - 7);
                setCalWeekStart(d.toISOString().slice(0, 10));
              };
              const nextWeek = () => {
                const d = new Date(calWeekStart + 'T12:00:00Z');
                d.setUTCDate(d.getUTCDate() + 7);
                setCalWeekStart(d.toISOString().slice(0, 10));
              };

              return (
                <div style={{ ...bubbleStyle, marginBottom: 24 }}>
                  {/* Week navigation */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <button onClick={prevWeek} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: NAVY, padding: '4px 8px' }}>‹</button>
                    <span style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{weekLabel}</span>
                    <button onClick={nextWeek} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: NAVY, padding: '4px 8px' }}>›</button>
                  </div>

                  {/* Day headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((name, i) => {
                      const day = weekDays[i];
                      const dayNum = day ? parseInt(day.date.slice(8, 10)) : '';
                      const isToday = day?.date === new Date().toISOString().slice(0, 10);
                      return (
                        <div key={i} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8' }}>{name}</div>
                          <div style={{
                            fontSize: 14, fontWeight: 700,
                            color: isToday ? '#fff' : day?.isWeekend ? '#94a3b8' : NAVY,
                            background: isToday ? AMBER : 'transparent',
                            borderRadius: '50%', width: 26, height: 26, lineHeight: '26px',
                            margin: '2px auto 6px',
                          }}>{dayNum}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Day columns — show projects with names */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                    {weekDays.map((day, di) => {
                      const totalHours = day.sheets.reduce((s, sh) => s + sh.total_hours, 0);
                      return (
                        <div key={di} style={{
                          padding: '6px 4px', borderRadius: 8, minHeight: 70,
                          background: day.holiday ? '#f5f3ff' : day.isWeekend ? '#f8fafc' : '#fff',
                          border: `1px solid ${day.holiday ? '#ddd6fe' : BORDER_COLOR}`,
                        }}>
                          {day.holiday && (
                            <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 600, marginBottom: 4, lineHeight: 1.2, textAlign: 'center' }}>
                              🏖 {day.holiday}
                            </div>
                          )}

                          {/* Project entries with names */}
                          {day.sheets.map((sh, si) => (
                            <div key={si} style={{
                              marginBottom: 3, padding: '3px 5px', borderRadius: 4,
                              background: (CAL_STATUS_COLORS[sh.status] || '#94a3b8') + '18',
                              borderLeft: `3px solid ${CAL_STATUS_COLORS[sh.status] || '#94a3b8'}`,
                            }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
                                {sh.project_code || sh.project_name}
                              </div>
                              <div style={{ fontSize: 8, color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{CAL_STATUS_LABELS[sh.status] || sh.status}</span>
                                <span style={{ fontWeight: 600 }}>{sh.total_hours.toFixed(1)}h</span>
                              </div>
                            </div>
                          ))}

                          {/* Missing indicator */}
                          {!day.isWeekend && !day.holiday && day.sheets.length === 0 && (
                            <div style={{
                              padding: '3px 5px', borderRadius: 4, background: '#f1f5f9',
                              borderLeft: '3px solid #e2e8f0', textAlign: 'center',
                            }}>
                              <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>Sin tarjeta</div>
                            </div>
                          )}

                          {/* Total */}
                          {totalHours > 0 && (
                            <div style={{ fontSize: 9, fontWeight: 700, color: NAVY, textAlign: 'right', marginTop: 3 }}>
                              {totalHours.toFixed(1)}h
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    {[
                      { color: '#16a34a', label: 'Aprobado' },
                      { color: '#ca8a04', label: 'Sometido' },
                      { color: '#2563eb', label: 'Pend. Ingeniero' },
                      { color: '#7c3aed', label: 'Pend. Gerente' },
                      { color: '#0f766e', label: 'Rev. Final' },
                      { color: '#a78bfa', label: 'Borrador' },
                      { color: '#ea580c', label: 'Devuelto' },
                      { color: '#e2e8f0', label: 'Sin tarjeta' },
                    ].map(l => (
                      <span key={l.label} style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.color, display: 'inline-block' }} />
                        <span style={{ color: '#64748b' }}>{l.label}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Charts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              {/* Hours by project */}
              {nominaStats.byProject.length > 0 && (
                <div style={bubbleStyle}>
                  <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Horas por Proyecto</div>
                  <ResponsiveContainer width="100%" height={Math.max(150, nominaStats.byProject.length * 35)}>
                    <BarChart data={nominaStats.byProject} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="project_name" type="category" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="regular" name="Regular" stackId="a" fill={AMBER} />
                      <Bar dataKey="overtime" name="Extra" stackId="a" fill="#7c3aed" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                    <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: AMBER }} /> Regular
                    </span>
                    <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: '#7c3aed' }} /> Extra
                    </span>
                  </div>
                </div>
              )}

              {/* Hours by employee */}
              {nominaStats.byEmployee.length > 0 && (
                <div style={bubbleStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>Horas por Empleado</div>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{nominaStats.byEmployee.length} registros</span>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 8, border: `1px solid ${BORDER_COLOR}` }}>
                    <ResponsiveContainer width="100%" height={Math.max(150, nominaStats.byEmployee.length * 35)}>
                      <BarChart data={nominaStats.byEmployee} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" orientation="top" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="employee_name" type="category" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip />
                        <Bar dataKey="regular" name="Regular" stackId="a" fill={AMBER} />
                        <Bar dataKey="overtime" name="Extra" stackId="a" fill="#7c3aed" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Recent sheets */}
            {nominaStats.recentSheets.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, color: NAVY, fontSize: 13, marginBottom: 12 }}>Tarjetas Recientes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {nominaStats.recentSheets.map(s => {
                    const stColor = NOMINA_STATUS_COLORS[s.status] || '#94a3b8';
                    const isEditable = s.status === 'draft' || s.status === 'returned';
                    return (
                      <div key={s.id} style={{
                        ...bubbleStyle, padding: '14px 18px', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center',
                        cursor: 'pointer', borderLeft: `3px solid ${stColor}`,
                      }}
                        onClick={() => navigate(isEditable ? `/horas/tarjeta/${s.id}` : `/horas/detalle/${s.id}`)}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>
                            {s.project_code ? `${s.project_code} ` : ''}{s.project_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {new Date(s.sheet_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                            {' · '}{s.employee_count} empleado{s.employee_count !== 1 ? 's' : ''}
                            {' · '}{parseFloat(s.total_hours).toFixed(1)}h
                          </div>
                        </div>
                        <span style={{
                          padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: stColor + '18', color: stColor,
                        }}>
                          {NOMINA_STATUS_LABELS[s.status] || s.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Gastos Tab (original) ──
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: AMBER }} />
      </div>
    );
  }
  if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Error al cargar estadísticas</div>;

  const { byStatus, byProject, byPaymentMethod, byChofer, byTarea, byCostCode, byGLCode,
          pendingMyApproval, pendingReviewCount, recentReceipts, totals } = stats;

  // All unique statuses for stacked bars
  const allStatuses = [...new Set(byStatus.map(s => s.status))];

  // Helper: build stacked data from rows with { key, status, total }
  function buildStacked(rows, keyFn) {
    const map = {};
    for (const row of rows) {
      const key = keyFn(row);
      if (!map[key]) map[key] = { name: key };
      const st = row.status || 'pending';
      map[key][st] = (map[key][st] || 0) + (parseFloat(row.total) || 0);
      map[key]._total = (map[key]._total || 0) + (parseFloat(row.total) || 0);
    }
    return Object.values(map).sort((a, b) => (b._total || 0) - (a._total || 0));
  }

  // Stacked: Payment Method × Status
  const paymentStacked = buildStacked(byPaymentMethod, r =>
    r.payment_method === 'credit_card' ? 'T. Crédito' : r.payment_method === 'petty_cash' ? 'Petty Cash' : r.payment_method || 'Otro'
  );

  // Stacked: Project × Status
  const projectStacked = buildStacked(byProject, r => r.project_name || 'Sin Proyecto').slice(0, 10);

  // Stacked: Chofer × Status
  const choferStacked = buildStacked(byChofer, r => r.chofer_name || '?').slice(0, 8);

  // Navigate to receipt detail
  const getReceiptPath = (r) => {
    if (isAdm) return `/admin/receipt/${r.id}`;
    if (isEng || isRev) return `/supervisor/receipt/${r.id}`;
    return `/employee/receipt/${r.id}`;
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0, marginRight: 16 }}>Dashboard</h2>
          {hasNomina && (
            <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER_COLOR}` }}>
              <button onClick={() => setTab('gastos')} style={tabBtn('gastos', 'Gastos')}>Gastos</button>
              <button onClick={() => setTab('nomina')} style={tabBtn('nomina', 'Nómina')}>Nómina</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={gastosProjectFilter}
            onChange={e => setGastosProjectFilter(e.target.value)}
            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${BORDER_COLOR}`, color: NAVY, background: '#f8fafc' }}
          >
            <option value="">Todos los proyectos</option>
            {gastosProjects.map(p => (
              <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ''}{p.name}</option>
            ))}
          </select>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); }} />
          {(isAdm || isRev || isEng) && (
            <button onClick={handleExport} disabled={exporting} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: exporting ? '#94a3b8' : NAVY, color: '#fff',
              border: 'none', cursor: exporting ? 'not-allowed' : 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}>
              <Download size={13} /> {exporting ? 'Exportando…' : 'Exportar CSV'}
            </button>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Recibos" value={totals.count} subtitle={fmt(totals.total)} subtitleLarge />
        {(isEng) && (
          <StatCard label="Pendiente Mi Aprobación" value={pendingMyApproval} highlight />
        )}
        {(isEng || isRev) && (
          <StatCard label="Pendiente Revisión Final" value={pendingReviewCount} highlight={isRev} />
        )}
        <StatCard label="Pendientes" value={byStatus.filter(s => ['pending', 'partially_approved', 'pending_review'].includes(s.status)).reduce((a, s) => a + s.count, 0)} subtitle={fmt(totals.pending)} subtitleLarge />
        <StatCard label="Aprobados" value={byStatus.find(s => s.status === 'approved')?.count || 0} subtitle={fmt(totals.approved)} subtitleLarge />
        <StatCard label="Pagados" value={byStatus.find(s => s.status === 'paid')?.count || 0} subtitle={fmt(totals.paid)} subtitleLarge />
      </div>

      {/* Stacked bar legend */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {allStatuses.map(st => (
          <div key={st} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR_MAP[st] || '#94a3b8', display: 'inline-block' }} />
            <span style={{ color: '#64748b' }}>{STATUS_LABEL[st] || st}</span>
          </div>
        ))}
      </div>

      {/* Charts row — all stacked by status */}
      <div style={{ display: 'grid', gridTemplateColumns: (isEng || isRev) ? '1fr 1fr 1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Status × Payment Method (stacked horizontal bar) */}
        {paymentStacked.length > 0 && (
          <div style={bubbleStyle}>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Estado por Método de Pago</div>
            <ResponsiveContainer width="100%" height={Math.max(120, paymentStacked.length * 50)}>
              <BarChart data={paymentStacked} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={75} />
                <Tooltip content={<ChartTooltip />} />
                {allStatuses.map(st => (
                  <Bar key={st} dataKey={st} name={STATUS_LABEL[st] || st} stackId="a" fill={STATUS_COLOR_MAP[st] || '#94a3b8'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Status × Project (stacked horizontal bar) */}
        {projectStacked.length > 0 && (
          <div style={bubbleStyle}>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Estado por Proyecto</div>
            <ResponsiveContainer width="100%" height={Math.max(150, projectStacked.length * 35)}>
              <BarChart data={projectStacked} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                <Tooltip content={<ChartTooltip />} />
                {allStatuses.map(st => (
                  <Bar key={st} dataKey={st} name={STATUS_LABEL[st] || st} stackId="a" fill={STATUS_COLOR_MAP[st] || '#94a3b8'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Status × Chofer (stacked) — engineer/reviewer only */}
        {(isEng || isRev) && choferStacked.length > 0 && (
          <div style={bubbleStyle}>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Estado por Chofer</div>
            <ResponsiveContainer width="100%" height={Math.max(150, choferStacked.length * 35)}>
              <BarChart data={choferStacked} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                <Tooltip content={<ChartTooltip />} />
                {allStatuses.map(st => (
                  <Bar key={st} dataKey={st} name={STATUS_LABEL[st] || st} stackId="a" fill={STATUS_COLOR_MAP[st] || '#94a3b8'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tarea / Cost Code / GL — stacked bar charts by project — for engineer/reviewer */}
      {(isEng || isRev) && (() => {
        const CHART_COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#d97706', '#dc2626', '#22c55e', '#6366f1', '#eab308', '#94a3b8'];

        // Build stacked: project on Y-axis, tareas as segments
        function buildProjectStacked(rows, keyFn) {
          const allKeys = new Set();
          const map = {};
          for (const row of rows) {
            const proj = row.project_name || '(Sin Proyecto)';
            const key = keyFn(row);
            allKeys.add(key);
            if (!map[proj]) map[proj] = { name: proj, _total: 0 };
            map[proj][key] = (map[proj][key] || 0) + (parseFloat(row.total) || 0);
            map[proj]._total += parseFloat(row.total) || 0;
          }
          return { data: Object.values(map).sort((a, b) => b._total - a._total).slice(0, 8), keys: [...allKeys].sort() };
        }

        const tareaChart = buildProjectStacked(byTarea, r => r.tarea_name);
        const ccChart = buildProjectStacked(byCostCode, r => r.cost_center);

        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))`, gap: 16, marginBottom: 24 }}>
            {tareaChart.data.length > 0 && (
              <div style={bubbleStyle}>
                <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Proyecto × Tarea</div>
                <ResponsiveContainer width="100%" height={Math.max(150, tareaChart.data.length * 35)}>
                  <BarChart data={tareaChart.data} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip content={<ChartTooltip />} />
                    {tareaChart.keys.map((k, i) => (
                      <Bar key={k} dataKey={k} name={k} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {tareaChart.keys.map((k, i) => (
                    <span key={k} style={{ fontSize: 8, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span style={{ color: '#64748b' }}>{k}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {ccChart.data.length > 0 && (
              <div style={bubbleStyle}>
                <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Proyecto × Centro de Costo</div>
                <ResponsiveContainer width="100%" height={Math.max(150, ccChart.data.length * 35)}>
                  <BarChart data={ccChart.data} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip content={<ChartTooltip />} />
                    {ccChart.keys.map((k, i) => (
                      <Bar key={k} dataKey={k} name={k} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {ccChart.keys.map((k, i) => (
                    <span key={k} style={{ fontSize: 8, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span style={{ color: '#64748b' }}>{k}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {isRev && byGLCode.length > 0 && (() => {
              const glChart = buildProjectStacked(byGLCode, r => r.gl_code);
              return (
                <div style={bubbleStyle}>
                  <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 13 }}>Proyecto × Código GL</div>
                  <ResponsiveContainer width="100%" height={Math.max(150, glChart.data.length * 35)}>
                    <BarChart data={glChart.data} layout="vertical" margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip content={<ChartTooltip />} />
                      {glChart.keys.map((k, i) => (
                        <Bar key={k} dataKey={k} name={k} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {glChart.keys.map((k, i) => (
                      <span key={k} style={{ fontSize: 8, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span style={{ color: '#64748b' }}>{k}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Receipts list — flat cards like admin view */}
      {recentReceipts && recentReceipts.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, color: NAVY, fontSize: 13, marginBottom: 12 }}>Recibos Recientes</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recentReceipts.map(r => (
              <div key={r.id} style={{
                ...bubbleStyle, padding: '14px 18px', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.1s',
              }}
                onClick={() => navigate(getReceiptPath(r), { state: { from: '/dashboard' } })}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
                      {r.receipt_number ? `#${r.receipt_number}` : ''}
                      {' — '}
                      {r.vendor || '(Sin proveedor)'}
                    </span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                    {r.employee_name || '—'}
                    {r.date && <span> | {new Date(String(r.date).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-PR')}</span>}
                    {r.payment_method && <span> | {PAYMENT_METHODS[r.payment_method] || r.payment_method}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: 16, color: NAVY }}>{fmt(r.amount)}</span>
                  <button style={{
                    background: NAVY, color: '#fff', border: 'none', borderRadius: 6,
                    padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>Ver</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
